// ./src/components/features/EmailTemplates/EmailTemplateSelector/EmailTemplateSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { useEmailTemplates } from "@/hooks/useEmailTemplates";
import { LoadingIndicator } from "@/components/generic";
import { EmailTemplate, isSystemTemplate, TemplateType } from "@/utils/api";
import { useApp } from "@/contexts";
import { STATUSES } from "@sdflc/utils";
import { usePremiumField } from "@/hooks";

export interface EmailTemplateSelectorProps {
  /** Selected email template ID */
  value: string | null | undefined;

  /** Callback when email template selection changes */
  onChange?: (
    emailTemplateId: string | null,
    emailTemplate?: EmailTemplate
  ) => void;

  /** Label for the select field */
  label?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Description text below the field */
  description?: string;

  /** Filter templates by type (notification, confirmation, etc.) */
  templateType?: TemplateType | string;

  /** Whether to include inactive templates */
  includeInactive?: boolean;

  /** Custom filter function for templates */
  templateFilter?: (template: EmailTemplate) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  premiumFeatureEnabled?: boolean | null | undefined;

  /** Additional props passed to Select */
  [key: string]: any;
}

export const EmailTemplateSelector: React.FC<EmailTemplateSelectorProps> = ({
  value,
  onChange,
  label = "Email Template",
  placeholder = "Select an email template",
  description,
  templateType,
  includeInactive = false,
  templateFilter,
  required = false,
  loadingPlaceholder = "Loading templates...",
  emptyText = "No templates available",
  error,
  disabled,
  premiumFeatureEnabled,
  size = "md",
  ...selectProps
}) => {
  const { user } = useApp();
  const userId = user?.id;

  // Build initial filters
  const initialFilters = useMemo(() => {
    const filters: any = {};

    if (!includeInactive) {
      filters.status = [STATUSES.ACTIVE];
    }

    if (templateType) {
      filters.templateType = [templateType];
    }

    return filters;
  }, [includeInactive, templateType]);

  const { emailTemplates, loading, isEmpty, lastError } = useEmailTemplates({
    autoLoad: true,
    userId,
    initialFilters,
  });

  // Filter and prepare template options
  const templateOptions = useMemo(() => {
    let filteredTemplates = emailTemplates;

    // Apply template type filter (in case initialFilters didn't catch it)
    if (templateType) {
      filteredTemplates = filteredTemplates.filter(
        (t) => t.templateType === templateType
      );
    }

    // Apply custom filter if provided
    if (templateFilter) {
      filteredTemplates = filteredTemplates.filter(templateFilter);
    }

    // Sort: default templates first, then user templates, then system templates, alphabetically within each group
    filteredTemplates.sort((a, b) => {
      // Default templates first
      if (a.isDefault !== b.isDefault) {
        return a.isDefault ? -1 : 1;
      }

      const aIsSystem = isSystemTemplate(a);
      const bIsSystem = isSystemTemplate(b);

      // User templates before system templates
      if (aIsSystem !== bIsSystem) {
        return aIsSystem ? 1 : -1;
      }

      // Alphabetically within each group
      const nameA = a.name || "Unnamed Template";
      const nameB = b.name || "Unnamed Template";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredTemplates.map((template) => {
      let label = template.name || "Unnamed Template";

      const badges: string[] = [];
      if (template.isDefault) badges.push("Default");
      if (isSystemTemplate(template)) badges.push("System");

      if (badges.length > 0) {
        label = `${label} (${badges.join(", ")})`;
      }

      return {
        value: template.id || "",
        label,
        disabled: template.status !== STATUSES.ACTIVE,
      };
    });
  }, [emailTemplates, templateType, templateFilter]);

  // Create a map for quick template lookup by ID
  const templateMap = useMemo(() => {
    const map = new Map<string, EmailTemplate>();
    emailTemplates.forEach((template) => {
      if (template.id) {
        map.set(template.id, template);
      }
    });
    return map;
  }, [emailTemplates]);

  // Auto-select default template if required and no value provided
  useEffect(() => {
    if (
      required &&
      !value &&
      templateOptions.length > 0 &&
      !loading &&
      onChange
    ) {
      // Try to find a default template first
      const defaultTemplate = templateOptions.find((opt) => {
        const template = templateMap.get(opt.value);
        return template?.isDefault && !opt.disabled;
      });

      const firstTemplate =
        defaultTemplate || templateOptions.find((opt) => !opt.disabled);

      if (firstTemplate) {
        const templateObject = templateMap.get(firstTemplate.value);
        onChange(firstTemplate.value, templateObject);
      }
    }
  }, [required, value, templateOptions.length, loading]);

  // Handle onChange with template object lookup
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedTemplate = templateMap.get(selectedValue);
      onChange?.(selectedValue, selectedTemplate);
    } else {
      onChange?.(null, undefined);
    }
  };

  // Determine select state
  const isInitialLoading = loading && emailTemplates.length === 0;
  const hasError = lastError && !loading;
  const isEmptyState = isEmpty && !loading;

  const selectState = {
    data: isInitialLoading || hasError || isEmptyState ? [] : templateOptions,
    placeholder: isInitialLoading
      ? loadingPlaceholder
      : hasError
      ? "Error loading templates"
      : isEmptyState
      ? emptyText
      : placeholder,
    disabled: disabled || isInitialLoading || hasError || isEmptyState,
    error: hasError ? error || "Failed to load templates" : error,
    rightSection:
      isInitialLoading || (loading && emailTemplates.length > 0) ? (
        <LoadingIndicator size={size} color="gray" />
      ) : (
        selectProps.rightSection
      ),
  };

  const premium = usePremiumField({
    label,
    description,
    premiumFeatureEnabled,
  });

  return (
    <Select
      {...selectProps}
      label={premium.label}
      description={premium.description}
      size={size}
      data={selectState.data}
      value={value ?? null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={premiumFeatureEnabled === false || selectState.disabled}
      error={selectState.error}
      rightSection={selectState.rightSection}
    />
  );
};
